import { useEffect, useState } from "react";

import { shares, type DeviceShare } from "../lib/api.js";

/**
 * Who else can see this device.
 *
 * Shown only to the owning organization's admins (and platform admins) -- the
 * API returns 404 to everyone else, including the organizations a device is
 * shared *with*, which have no business knowing who else it reaches.
 */
export function SharePanel({ deviceId }: { deviceId: string }) {
  const [list, setList] = useState<DeviceShare[] | null>(null);
  const [targets, setTargets] = useState<{ id: string; name: string }[]>([]);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [target, setTarget] = useState("");
  const [permission, setPermission] = useState<"read" | "control">("read");
  const [alertsEnabled, setAlertsEnabled] = useState(false);

  const load = () => {
    shares
      .list(deviceId)
      .then((r) => setList(r.shares))
      // A 404 here means "not yours to share", which is the normal case for
      // most viewers -- the panel simply does not appear.
      .catch(() => setDenied(true));
    shares.targets(deviceId).then((r) => setTargets(r.organizations)).catch(() => setTargets([]));
  };

  useEffect(load, [deviceId]);

  if (denied || !list) return null;

  const add = async () => {
    if (!target) return;
    setError(null);
    try {
      await shares.create(deviceId, { tenant_id: target, permission, alerts_enabled: alertsEnabled });
      setAdding(false);
      setTarget("");
      setPermission("read");
      setAlertsEnabled(false);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not share");
    }
  };

  const revoke = async (share: DeviceShare) => {
    if (
      !confirm(
        `Stop sharing this device with ${share.tenant_name}?\n\n` +
          `They lose access immediately, and any alert rules they wrote for it are deleted.`,
      )
    ) {
      return;
    }
    setError(null);
    try {
      await shares.remove(deviceId, share.id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not revoke");
    }
  };

  return (
    <section style={card}>
      <h2 style={{ fontSize: 13, margin: "0 0 4px", letterSpacing: "0.12em" }}>&gt; SHARED WITH</h2>
      <p style={{ color: "var(--text-muted)", fontSize: 11, margin: "0 0 12px", lineHeight: 1.5 }}>
        Other organizations that can see this device. They never see its
        credentials and cannot change its configuration; you can withdraw
        access at any time.
      </p>

      {error && <p style={{ color: "var(--status-critical)", fontSize: 12 }}>{`!! ${error}`}</p>}

      {list.length === 0 && (
        <p style={{ color: "var(--text-muted)", fontSize: 12, margin: "0 0 10px" }}>
          -- NOT SHARED --
        </p>
      )}

      {list.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 10 }}>
          <tbody>
            {list.map((share) => (
              <tr key={share.id} style={{ borderBottom: "1px solid var(--gridline)" }}>
                <td style={{ padding: "7px 0" }}>{share.tenant_name}</td>
                <td style={{ padding: "7px 8px" }}>
                  <select
                    value={share.permission}
                    onChange={(e) =>
                      void shares
                        .update(deviceId, share.id, { permission: e.target.value as "read" | "control" })
                        .then(load)
                    }
                    style={select}
                  >
                    <option value="read">READ</option>
                    <option value="control">CONTROL</option>
                  </select>
                </td>
                <td style={{ padding: "7px 8px" }}>
                  <label style={{ display: "flex", gap: 5, alignItems: "center", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={share.alerts_enabled}
                      onChange={(e) =>
                        void shares
                          .update(deviceId, share.id, { alerts_enabled: e.target.checked })
                          .then(load)
                      }
                    />
                    <span style={{ color: "var(--text-muted)" }}>MAY ALERT</span>
                  </label>
                </td>
                <td style={{ padding: "7px 0", textAlign: "right" }}>
                  <button onClick={() => void revoke(share)} style={{ ...smallButton, color: "var(--status-critical)" }}>
                    [ REVOKE ]
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding ? (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select value={target} onChange={(e) => setTarget(e.target.value)} style={select}>
            <option value="">-- ORGANIZATION --</option>
            {targets
              .filter((t) => !list.some((s) => s.tenant_id === t.id))
              .map((t) => (
                <option key={t.id} value={t.id}>{t.name.toUpperCase()}</option>
              ))}
          </select>
          <select value={permission} onChange={(e) => setPermission(e.target.value as "read" | "control")} style={select}>
            <option value="read">READ</option>
            <option value="control">CONTROL</option>
          </select>
          <label style={{ display: "flex", gap: 5, alignItems: "center", fontSize: 11, cursor: "pointer" }}>
            <input type="checkbox" checked={alertsEnabled} onChange={(e) => setAlertsEnabled(e.target.checked)} />
            MAY ALERT
          </label>
          <button disabled={!target} onClick={() => void add()} style={smallButton}>[ SHARE ]</button>
          <button onClick={() => setAdding(false)} style={smallButton}>[ CANCEL ]</button>
        </div>
      ) : (
        <button onClick={() => setAdding(true)} style={smallButton}>[ + SHARE WITH ORGANIZATION ]</button>
      )}
    </section>
  );
}

const card: React.CSSProperties = {
  border: "1px solid var(--border)", background: "var(--surface-1)", padding: 14, marginBottom: 16,
};
const select: React.CSSProperties = {
  padding: "5px 7px", fontSize: 11, border: "1px solid var(--border)",
  background: "var(--surface-raised)", color: "var(--text-primary)", letterSpacing: "0.06em",
};
const smallButton: React.CSSProperties = {
  background: "none", border: "1px solid var(--border)", padding: "5px 9px",
  fontSize: 11, color: "var(--text-secondary)", cursor: "pointer", letterSpacing: "0.08em",
};
